import { useEffect, useRef, useState, type RefObject } from 'react';
import Hls from 'hls.js';
import type { PlaybackResponse, VideoStatus } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Délai au-delà duquel une source `raw` qui n'a toujours pas livré ses métadonnées est tenue pour
 * injouable ici. Un MP4 non « faststart » (atome `moov` en fin de fichier, cas courant des sources
 * drone) ne lève aucune erreur : le `<video>` télécharge indéfiniment sans jamais émettre
 * `loadedmetadata`. Ne concerne plus que les vidéos **pas encore transcodées** (voir ci-dessous) :
 * dès qu'une vidéo est `READY`, on lit directement le HLS sans toucher à la source.
 */
const RAW_METADATA_TIMEOUT_MS = 8_000;

/**
 * Source active du `<video>` d'annotation :
 * - `raw` : fichier d'origine (pleine résolution, disponible dès l'upload) ;
 * - `hls` : rendu HLS H.264 produit par le worker (ADR-0007), lisible partout ;
 * - `pending` : la source n'est pas décodable ici et le transcodage n'est pas terminé.
 */
export type AnnotationVideoMode = 'raw' | 'hls' | 'pending';

export interface AnnotationVideo {
  mode: AnnotationVideoMode;
  /** Couverture (`cover.jpg`, image à 10 % de la durée) à poser en `poster` — non noire. */
  posterUrl: string | null;
}

/**
 * Alimente le `<video>` d'annotation avec une source **lisible par le navigateur**.
 *
 * Dès qu'une vidéo est `READY`, on sert directement le **rendu HLS H.264** : il démarre vite
 * (segments courts, adaptatif), se lit partout, et évite d'attendre puis d'échouer sur une source
 * drone souvent HEVC et/ou non « faststart » — qui, décodée progressivement, soit lève une erreur,
 * soit se télécharge sans fin (spinner éternel sur Safari iOS). La source d'origine n'est utilisée
 * que **tant que le transcodage n'est pas terminé** (annotation anticipée) ; si elle est illisible
 * ici, on signale que la conversion est en cours.
 *
 * Le `<video>` alimenté par hls.js l'est via **MSE** (données fournies par script) : le canevas
 * d'extraction d'images reste « propre » (non teinté), donc `Extraire l'image` continue de marcher.
 */
export function useAnnotationVideo(
  videoRef: RefObject<HTMLVideoElement | null>,
  videoId: string,
  rawUrl: string | null,
  status: VideoStatus | undefined,
): AnnotationVideo {
  const [mode, setMode] = useState<AnnotationVideoMode>('raw');
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Vidéo transcodée : on va droit au HLS (et on récupère au passage la couverture pour le poster).
  // Sinon on repart de la source d'origine, seul contenu disponible avant le transcodage.
  useEffect(() => {
    setHlsUrl(null);
    setPosterUrl(null);
    if (status !== 'READY') {
      setMode('raw');
      return;
    }
    let active = true;
    apiFetch<PlaybackResponse>(`/videos/${videoId}/playback`)
      .then((playback) => {
        if (!active) return;
        setHlsUrl(playback.hlsUrl);
        setPosterUrl(playback.coverUrl);
        setMode('hls');
      })
      .catch(() => {
        // HLS indisponible (aléa réseau) : on signale « en traitement » plutôt qu'une page muette.
        if (active) setMode('pending');
      });
    return () => {
      active = false;
    };
  }, [videoId, status]);

  // Source d'origine (uniquement avant transcodage), posée impérativement.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || mode !== 'raw' || !rawUrl || status === 'READY') return;
    video.src = rawUrl;
  }, [videoRef, rawUrl, mode, status]);

  // Source brute injouable ici (codec non décodable, ou métadonnées jamais livrées) : comme le
  // transcodage n'est pas terminé, il n'y a pas de HLS de repli — on signale « en traitement ».
  useEffect(() => {
    const video = videoRef.current;
    if (!video || mode !== 'raw' || status === 'READY') return;
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return;

    const bail = () => setMode('pending');
    const onError = () => {
      // 3 = MEDIA_ERR_DECODE, 4 = MEDIA_ERR_SRC_NOT_SUPPORTED ; 2 = réseau (ne pas basculer).
      const code = video.error?.code;
      if (code === 3 || code === 4) bail();
    };
    const timer = setTimeout(bail, RAW_METADATA_TIMEOUT_MS);
    const cancel = () => clearTimeout(timer);
    video.addEventListener('error', onError);
    video.addEventListener('loadedmetadata', cancel);
    return () => {
      clearTimeout(timer);
      video.removeEventListener('error', onError);
      video.removeEventListener('loadedmetadata', cancel);
    };
  }, [videoRef, mode, status]);

  // Attache hls.js une fois en mode HLS.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || mode !== 'hls' || !hlsUrl) return;

    // Safari lit le HLS nativement (et décode déjà le H.265).
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.removeAttribute('src');
      video.src = hlsUrl;
      return;
    }
    if (!Hls.isSupported()) return;

    video.removeAttribute('src');
    const hls = new Hls({ maxBufferLength: 30, enableWorker: true });
    hls.loadSource(hlsUrl);
    hls.attachMedia(video);
    // Annotation : on épingle la variante la plus haute pour préserver le détail (petits objets).
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      hls.currentLevel = hls.levels.length - 1;
    });
    hlsRef.current = hls;

    return () => {
      hls.destroy();
      hlsRef.current = null;
    };
  }, [videoRef, hlsUrl, mode]);

  return { mode, posterUrl };
}
